import { TrashIcon } from '@heroicons/react/24/outline';
import { type FC, type KeyboardEvent, type MutableRefObject, type ReactNode, useState, useRef, useEffect } from 'react';

import { Button } from '../Button/Button';
import { Input } from '../Input/Input';
import { Modal, ModalFooter } from '../Modal/Modal';

export interface ConfirmDeleteDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when the dialog should close
   */
  onClose: () => void;

  /**
   * Callback when delete is confirmed
   */
  onConfirm: () => void;

  /**
   * Dialog title
   */
  title: string;

  /**
   * The name that must be typed exactly to confirm deletion
   */
  itemName: string;

  /**
   * Optional description/warning message
   */
  message?: ReactNode;

  /**
   * Delete button text
   * @default 'Delete'
   */
  confirmText?: string;

  /**
   * Cancel button text
   * @default 'Cancel'
   */
  cancelText?: string;

  /**
   * Whether the delete action is in progress
   * @default false
   */
  isLoading?: boolean;
}

/**
 * ConfirmDeleteDialog component
 *
 * A modal dialog for confirming destructive actions by requiring
 * the user to type the exact name of the item being deleted.
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <ConfirmDeleteDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={() => {
 *     handleDelete();
 *     setIsOpen(false);
 *   }}
 *   title="Delete Investment"
 *   itemName="My Investment Project"
 *   message="This action cannot be undone. All associated data will be permanently deleted."
 * />
 * ```
 */
export const ConfirmDeleteDialog: FC<ConfirmDeleteDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isLoading = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Reset input when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  const isNameMatch = inputValue === itemName;

  const handleConfirm = () => {
    if (isNameMatch && !isLoading) {
      onConfirm();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && isNameMatch && !isLoading) {
      onConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      showCloseButton={false}
      initialFocus={cancelButtonRef as MutableRefObject<HTMLElement | null>}
    >
      <div className="flex gap-4">
        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30">
          <TrashIcon className="w-6 h-6 text-red-600 dark:text-red-400" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          {message && (
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">{message}</div>
          )}
          <div className="mt-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              To confirm, please type{' '}
              <span className="font-semibold text-gray-900 dark:text-gray-100">{itemName}</span>{' '}
              below:
            </p>
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={itemName}
              disabled={isLoading}
              autoComplete="off"
              size="md"
            />
            {inputValue && !isNameMatch && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">Name does not match</p>
            )}
          </div>
        </div>
      </div>

      <ModalFooter>
        <Button
          ref={cancelButtonRef}
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={handleConfirm}
          disabled={!isNameMatch || isLoading}
        >
          {isLoading ? 'Deleting...' : confirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ConfirmDeleteDialog.displayName = 'ConfirmDeleteDialog';
